import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {ApiService, IFile, IImgUrls} from './api.service';
import {catchError, EMPTY, tap} from 'rxjs';
import {LoadingService} from './loading.service';
import {AuthService} from './auth.service';
import {BriefsService} from './briefs.service';
import {WsService} from './ws.service';
import {HelpersService} from './helpers.service';
import {MessagesService,} from './messages.service';
import {IMessage} from './messages-receivers/imessage.interface';
import { DisputesApiService } from './disputes.service';

export interface IParticipant {
  id: number;
  fio: string;
  lastOnline: string;
  image: IImgUrls;
}

export interface IChatsAll {
  id: number;
  briefId?: number;
  integrationId: number;
  integrationStatus: string;
  createdAt: string;
  initiatorId: number;
  isActive: boolean;
  lastMessage: string;
  lastMessageType: string;
  lastMessageAt: string;
  unreadCount: number;
  participants: {
    initiator: IParticipant;
    receiver: IParticipant;
  };
  isDispute?: boolean;//потом убрать
  type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatsApiService {
  loadingService = inject(LoadingService);
  apiService = inject(ApiService);
  authService = inject(AuthService);
  messagesService = inject(MessagesService)
  helpersService = inject(HelpersService);

  User = this.authService.user

  MessagesReceiver = this.messagesService.chatReceiver

  GetChatsListAll(
    filter: string,
  ) {
    const queryString = filter ? `?${new URLSearchParams({filter}).toString()}` : '';

    return this.apiService.SendGet(`/user/chats${queryString}`, this.loadingService.StatesNames.CHATS_GET_ALL);
  }

  GetUnreadMessages() {
    return this.apiService.SendGet(`/${this.User()?.role?.toLowerCase()}/unread-messages`, this.loadingService.StatesNames.CHATS_UNREAD_MESSAGES);
  }

  GetChatsListBriefs(id: number) {
    return this.apiService.SendGet(`/seller/brief/${id}/chat`, this.loadingService.StatesNames.CHATS_GET_BRIEFS);
  }

  GetChatById(id: number,) {
    return this.apiService.SendGet(`/user/chat/${id}`, this.loadingService.StatesNames.CHATS_GET_ITEM);
  }

  GetChatId(id: number) {
    return this.apiService.SendGet(`/user/chatId/${id}/`, this.loadingService.StatesNames.CHATS_GET_ID);
  }

  GetMessagesList(chatId: number, offsetMessageId: number | null = null) {
    return this.apiService.SendGet(`/user/chat/${chatId}/history?${offsetMessageId ? `offsetMessageId=${offsetMessageId}` : ''}`,);
  }

  DownloadMessageFile(id: number) {
    return this.apiService.SendGetBlobImg(`/message-storage/${id}`, this.loadingService.StatesNames.MESSAGE_DOWNLOAD_FILE);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ChatsService {
  chatsApiService = inject(ChatsApiService);
  disputesApiService = inject(DisputesApiService);
  briefsService = inject(BriefsService);
  helpersService = inject(HelpersService);

  ChatsListAll = signal<IChatsAll[]>([]);
  ChatsListBriefs = signal<IChatsAll[]>([]);

  ChatInfo = signal<IChatsAll>({} as IChatsAll);

  Messages = this.chatsApiService.MessagesReceiver.eventsList
  Disputes = this.disputesApiService.MessagesReceiver.eventsList

  countListAll = signal<number>(0);
  countListChat = signal<number>(0);
  countListDispute = signal<number>(0);

  constructor() {
    effect(() => {
      const m = this.Messages();
      const lastMessage = m.at(-1);
      if (lastMessage) {
        this._handleNewMessage(lastMessage);
      }
    });

    effect(() => {
      const d = this.Disputes();
      const lastMessage = d.at(-1);
      if (lastMessage) {
        this._handleNewMessage(lastMessage);
      }
    });
  }

  private _handleNewMessage(lastMessage: IMessage): void {
    const isActiveChat = this.ChatInfo()?.id === lastMessage.chatId;
    this.ChatsListAll.update(chats => {
      const updatedChats = chats.map(chat => {
        if (lastMessage?.chatId === chat.id) {
          if (!isActiveChat) {
            this.countListAll.update(v => v + 1);
            if (chat.type === 'CHAT') {
              this.countListChat.update(v => v + 1);
            } else {
              this.countListDispute.update(v => v + 1);
            }
          }
          return {
            ...chat,
            unreadCount: isActiveChat ? chat.unreadCount : chat.unreadCount + 1,
            lastMessageAt: lastMessage.createdAt,
            lastMessage: lastMessage.content.text ?? 'Файл'
          }
        } else {
          return { ...chat }
        }
      });
      const updatedChat = updatedChats.find(chat => chat.id === lastMessage?.chatId);
      if (updatedChat) {
        const otherChats = updatedChats.filter(chat => chat.id !== lastMessage?.chatId);
        return [updatedChat, ...otherChats];
      }
      return updatedChats;
    });
  }

  getChatsListAll(
    {filter}:
      {
        filter: string;
      }
  ) {
    return this.chatsApiService.GetChatsListAll(filter).pipe(
      tap((res) => {
        this.ChatsListAll.set(res);
        // if (filter === 'ALL') {
        //   this.countListAll.set(
        //     this.ChatsListAll().reduce((sum, item) => sum + (item.unreadCount || 0), 0)
        //   );
        //   this.countListChat.set(
        //     this.ChatsListAll().filter(item => item.type === 'CHAT').reduce((sum, item) => sum + (item.unreadCount || 0), 0)
        //   );
        //   this.countListDispute.set(
        //     this.ChatsListAll().filter(item => item.type === 'DISPUTE_CHAT').reduce((sum, item) => sum + (item.unreadCount || 0), 0)
        //   );
        // }
      })
    )
  }

  getUnreadMessages() {
    return this.chatsApiService.GetUnreadMessages().pipe(
      tap((res) => {
        this.countListAll.set(res['totalUnreadMessages']);
        this.countListChat.set(res['chatUnreadMessages']);
        this.countListDispute.set(res['disputedUnreadMessages']);
      })
    )
  }

  getChatsListBriefs(
    {id}:
      {
        id: number;
      }
  ) {
    return this.chatsApiService.GetChatsListBriefs(id).pipe(
      tap((res) => {
        this.ChatsListBriefs.set(res);
      })
    )
  }

  getChatById(id: number) {
    return this.chatsApiService.GetChatById(id).pipe(
      tap((res) => {
        this.ChatInfo.set(res);
        this.countListAll.set(this.countListAll() - res['unreadCount']);
        this.countListChat.set(this.countListChat() - res['unreadCount']);
      })
    )
  }

  clearChat(): void {
    this.ChatInfo.set({} as IChatsAll);
  }

  getChatId(id: number) {
    return this.chatsApiService.GetChatId(id).pipe(
      tap((res) => {
        return res;
      })
    )
  }

  getMessages(chatId: number, offsetMessageId: number | null = null) {
    return this.chatsApiService.GetMessagesList(chatId, offsetMessageId).pipe(
      tap(
        (res) => {
          let ms: IMessage[] = res.content.map((x: {
            id: number,
            text: string,
            createdAt: string,
            isRead: boolean,
            sender: {
              id: number,
              name: string,
              role: string
            }
            files: {
              id: number,
              name: string,
              size: number,
              url: string,
              blurhash: string,
              contentType: string
            }[]
          }) => {
            const r: IMessage = {
              id: x.id,
              chatId: chatId,
              content: {
                text: x.text,
                files: x.files,
                isRead: x.isRead,
                sender: x.sender
              },
              createdAt: x.createdAt,
              updatedAt: x.createdAt
            }
            return r
          });

          for (let i = 0; i < ms.length; i++) {
            if (ms[i].content?.files) {
              for (let j = 0; j < ms[i].content.files!.length; j++) {
                ms[i].content.files![j].size = this.helpersService.formatBytes(ms[i].content.files![j].size as number)
              }
            }
          }

          this.Messages.set([...ms, ...this.Messages()]);
        }
      )
    )
  }

  sendMessage(message: {
    chatId: number
    text: string | null,
    files: IFile[] | null,
    event: 'NEW_MESSAGE'
  }) {
    this.chatsApiService.MessagesReceiver.sendMessage(
      {
        messageToHistory: this.chatsApiService.MessagesReceiver.outcomingEventToIMessage(
          message
        ),
        messageSend: {
          event: message.event,
          data: {
            chatId: message.chatId,
            text: message.text,
            files: message.files?.map((x: any) => x.id) ?? [],
          }
        }
      }
    );
  }

  sendMessageBrief(message: {
    briefId: number
    text: string | null,
    files: IFile[] | null,
    event: 'NEW_MESSAGE'
  }) {
    this.chatsApiService.MessagesReceiver.sendMessage(
      {
        messageToHistory: this.chatsApiService.MessagesReceiver.outcomingEventToIMessage(
          {...message, chatId: -1}
        ),
        messageSend: {
          event: message.event,
          data: {
            briefId: message.briefId,
            text: message.text,
            files: message.files?.map((x: any) => x.id) ?? [],
          }
        }
      }
    );
  }

  sendMessageIntegration(message: {
    integrationId: number
    text: string | null,
    files: IFile[] | null,
    event: 'NEW_MESSAGE'
  }) {
    this.chatsApiService.MessagesReceiver.sendMessage(
      {
        messageToHistory: this.chatsApiService.MessagesReceiver.outcomingEventToIMessage(
          {...message, chatId: -1}
        ),
        messageSend: {
          event: message.event,
          data: {
            integrationId: message.integrationId,
            text: message.text,
            files: message.files?.map((x: any) => x.id) ?? [],
          }
        }
      }
    );
  }

  downloadMessageFile(id: number) {
    return this.chatsApiService.DownloadMessageFile(id).pipe(
      tap((res) => {
        return res;
      })
    )
  }
}

